import { FREE_GIFT_IDS } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { DrawerFooterSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import {
	clearFreeGiftSelection,
	getProductDetailsByPartNumber,
	isFixedPurchaseGroupOutOfStock,
	isOutOfStock,
} from '@/components/blocks/FreeGiftV2/utils';
import { FormatPrice } from '@/components/blocks/HDMPriceDisplay/FormatPrice';
import { AddProductCart } from '@/components/blocks/ProductDetails/parts/AddProductCart';
import { usePdpDeliveryOptions } from '@/data/Content/PdpDeliveryOptions';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import { Box, Typography } from '@mui/material';
import { useContext, useEffect, useMemo } from 'react';

export const DrawerFooter = () => {
	const labels = useLocalization('FreeGift');
	const {
		currentPartNumber,
		promoItems,
		purchase,
		purchaseQty,
		othersQty,
		reward,
		rewardsQty,
		marketInventory,
		promoThdCode,
	} = useFreeGiftPromotion();
	const {
		promotionQty,
		selectedPurchaseGroupId,
		selectedPurchaseSkus,
		selectedRewardGroupId,
		selectedRewardSkus,
		handleCloseDrawer,
		setIsPromotionComplete,
		setHasAddedToCart,
	} = useFreeGiftInteraction();
	const { selection, product, isDeliverySelected, deliveryShipMode, pickupInStoreShipMode } =
		useContext(ContentContext) as ReturnType<typeof useProductDetails>;

	const { pickup, delivery } = usePdpDeliveryOptions(selection?.sku ?? product);
	const homeDeliveryDate =
		delivery.status === 'with-date' || delivery.status === 'same-day'
			? delivery.deliveryDate
			: undefined;
	const pickupDate = pickup.status === 'with-date' ? pickup.deliveryDate : undefined;

	const shipModeId = isDeliverySelected
		? deliveryShipMode?.shipModeId
		: pickupInStoreShipMode?.shipModeId;

	const selectedFixedGroup = purchase?.purchases?.find(
		(group) => group.triggerGroupId === selectedPurchaseGroupId
	);

	const combinableCandidates = purchase?.purchases?.[0]?.triggerSKUs ?? [];
	const isSoleCombinableCandidateCurrentPartOutOfStock =
		Boolean(purchase?.combinable) &&
		combinableCandidates.length === 1 &&
		combinableCandidates[0].sku === currentPartNumber &&
		isOutOfStock(marketInventory[currentPartNumber]);

	const isPurchaseComplete = purchase?.combinable
		? !isSoleCombinableCandidateCurrentPartOutOfStock &&
		  selectedPurchaseSkus.reduce((sum, item) => sum + item.quantity, 0) >=
				(purchaseQty + othersQty) * promotionQty
		: Boolean(
				selectedFixedGroup &&
					!isFixedPurchaseGroupOutOfStock(selectedFixedGroup, promotionQty, marketInventory)
		  );

	const isRewardComplete = reward?.combinable
		? selectedRewardSkus.reduce((sum, item) => sum + item.quantity, 0) >= rewardsQty * promotionQty
		: Boolean(selectedRewardGroupId);

	const isPromotionComplete = isPurchaseComplete && isRewardComplete;

	const isDirectPromotion =
		!purchase?.combinable &&
		purchase?.purchases.length === 1 &&
		!reward?.combinable &&
		reward?.rewards.length === 1;

	useEffect(() => {
		setIsPromotionComplete(isPromotionComplete);
	}, [isPromotionComplete, setIsPromotionComplete]);
	const totalPromotionPrice = useMemo(
		() =>
			selectedPurchaseSkus.reduce((sum, { sku, quantity }) => {
				const product = getProductDetailsByPartNumber(promoItems, sku);

				return sum + (product?.productPrice.offer ?? 0) * quantity;
			}, 0),
		[selectedPurchaseSkus, promoItems]
	);

	const totalSavings = useMemo(
		() =>
			selectedRewardSkus.reduce((sum, { sku, quantity }) => {
				const product = getProductDetailsByPartNumber(promoItems, sku);

				return sum + (product?.productPrice.offer ?? 0) * quantity;
			}, 0),
		[selectedRewardSkus, promoItems]
	);

	const { partNumbers, quantities } = useMemo(
		() => ({
			partNumbers: [
				...selectedRewardSkus.map((i) => i.sku),
				...selectedPurchaseSkus.map((i) => i.sku),
			],
			quantities: [
				...selectedRewardSkus.map((i) => String(i.quantity)),
				...selectedPurchaseSkus.map((i) => String(i.quantity)),
			],
		}),
		[selectedRewardSkus, selectedPurchaseSkus]
	);

	const freeGiftPartNumbers = useMemo(
		() => selectedRewardSkus.map((i) => i.sku),
		[selectedRewardSkus]
	);

	const trackedProducts = useMemo(
		() =>
			partNumbers
				.map((pn) => getProductDetailsByPartNumber(promoItems, pn))
				.filter((product): product is ProductType => Boolean(product)),
		[partNumbers, promoItems]
	);

	return (
		<Box sx={DrawerFooterSx.stickyFooter} id={FREE_GIFT_IDS.stickyContainer}>
			<Box display="flex" justifyContent="space-between">
				<Typography variant="bodySubText" color="success.main">
					{labels.Savings.t()}
				</Typography>
				<Typography variant="bodySubText" color="success.main" id={FREE_GIFT_IDS.savingsLabel}>
					<FormatPrice price={totalSavings} type="simple" />
				</Typography>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Typography variant="bodySubText" color="grey.500">
					{labels.TotalPromotion.t()}
				</Typography>
				<FormatPrice
					price={totalPromotionPrice}
					variant="sm"
					id={FREE_GIFT_IDS.totalProductLabel}
				/>
			</Box>
			<AddProductCart
				partNumber={partNumbers}
				quantity={quantities}
				wording={labels.AddAllToCart.t()}
				disabled={!isPromotionComplete}
				variant="contained"
				handleClose={() => {
					setHasAddedToCart(true);

					if (!isDirectPromotion) {
						clearFreeGiftSelection(currentPartNumber);
					}

					handleCloseDrawer();
				}}
				sx={DrawerFooterSx.button}
				availableQuantity={1}
				products={trackedProducts}
				freeProductPartnumber={freeGiftPartNumbers}
				freeProductPromoType={promoThdCode}
				id={FREE_GIFT_IDS.addPromoToCart}
				shipModeId={shipModeId}
				homeDeliveryDate={homeDeliveryDate}
				pickupDate={pickupDate}
			/>
		</Box>
	);
};
